/**
 * Designer3D — the 3D designer as a full-window modal.
 *
 * A thin host around `DesignerWorkspace`: it owns open/close (the `open-designer`
 * window event, fired from the super-admin Dev Console) and holds the room being
 * edited, with its undo history. It seeds a preset room for now; seeding from
 * the live estimate comes with the wizard integration.
 *
 * For day-to-day designer work use the sandbox page instead (`/sandbox/designer`,
 * see `pages/DesignerSandbox.tsx`) — same workspace, no wizard to click through.
 */

import React, { useEffect, useState } from 'react';
import { DesignerWorkspace } from './DesignerWorkspace';
import { DESIGNER_PRESETS } from './presets';
import { useDesignerHistory } from './useDesignerState';
import { RoomSurfaces } from '../../types/sections';

export const Designer3D: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [doc] = useState(() => DESIGNER_PRESETS[0].build());
  const history = useDesignerHistory<RoomSurfaces>(doc.room);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('open-designer', onOpen);
    return () => window.removeEventListener('open-designer', onOpen);
  }, []);

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }}>
      <DesignerWorkspace
        dims={doc.dims}
        room={history.present}
        onCommit={history.commit}
        onUndo={history.undo}
        onRedo={history.redo}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
        onClose={() => setOpen(false)}
      />
    </div>
  );
};

export default Designer3D;
